"use client";

type ClientErrorContext = Record<string, unknown> & {
  digest?: string | null;
};

const DEDUPE_WINDOW_MS = 5 * 60 * 1000;
const clientDedupeStore = new Map<string, number>();

function cleanupDedupeStore(now: number) {
  for (const [fingerprint, timestamp] of clientDedupeStore.entries()) {
    if (now - timestamp > DEDUPE_WINDOW_MS) {
      clientDedupeStore.delete(fingerprint);
    }
  }
}

function createFingerprint(error: Error & { digest?: string }) {
  return [
    error.name,
    error.message,
    (error.stack ?? "").slice(0, 200),
    error.digest ?? "",
  ].join("::");
}

export async function reportClientError(
  error: Error & { digest?: string },
  context: ClientErrorContext = {}
) {
  console.error(error);

  if (process.env.NODE_ENV !== "production") {
    return;
  }

  const now = Date.now();
  cleanupDedupeStore(now);
  const fingerprint = createFingerprint(error);
  const existingTimestamp = clientDedupeStore.get(fingerprint);

  if (existingTimestamp && now - existingTimestamp < DEDUPE_WINDOW_MS) {
    return;
  }

  clientDedupeStore.set(fingerprint, now);

  try {
    await fetch("/api/error-report", {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
          digest: error.digest ?? null,
        },
        context: {
          ...context,
          digest: error.digest ?? context.digest ?? null,
          request: {
            method: "GET",
            path: window.location.pathname,
          },
          source: "client",
        },
      }),
      keepalive: true,
    });
  } catch (reportingError) {
    console.error("[error-reporter-client] Versand fehlgeschlagen", reportingError);
  }
}
